import React, { useMemo, useState } from 'react';
import { ArrowLeft, ArrowRight, ShieldCheck, Check, ChevronDown, AlertCircle, Plus, Trash2 } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { submitQuiz } from '../lib/api';

// ============================================================================
// ALFÀ Premium 6-Part Weight-Loss Quiz
// Modelled 1-to-1 on the 34-screen reference the user provided.
// Branding: ivory (#faf5ec) bg · near-black text · monochrome (no gold accents) ·
// solid black CTA. Per-part progress bar (6 pills).
// ============================================================================

const ACCENT = '#0f1112'; // black — all icons and chart accents
const IVORY  = '#ffffff';

const PARTS = [
  { n: 1, label: 'Discover' },
  { n: 2, label: 'Composition' },
  { n: 3, label: 'Screening' },
  { n: 4, label: 'History' },
  { n: 5, label: 'Prescription' },
  { n: 6, label: 'You' },
];

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

// ---------------------------------------------------------------------------
// STEP DEFINITIONS
// ---------------------------------------------------------------------------
const STEPS = [
  // ---------- Part 1 · Discover (9) ----------
  { id: 'priority', part: 1, kind: 'single', autoAdvance: true,
    heading: <>Start your <span style={{ color: ACCENT }}>online medical intake.</span></>,
    subline: 'Your answers will be reviewed by a licensed provider, who determines whether treatment is medically appropriate. Completing this intake does not guarantee a prescription.',
    question: 'Which of these is your priority?',
    options: [
      { value: 'lose_weight', label: 'Lose weight' },
      { value: 'reduce_cravings', label: 'Reduce hunger and cravings' },
      { value: 'improve_health', label: 'Improve my overall health' },
    ] },
  { id: 'sex', part: 1, kind: 'tiles',
    heading: <>Tell us <span style={{ color: ACCENT }}>about yourself.</span></>,
    question: <>Are you <span style={{ color: ACCENT }}>male</span> or <span style={{ color: ACCENT }}>female</span>?</>,
    options: [
      { value: 'male',   label: 'Male',   icon: MaleIcon },
      { value: 'female', label: 'Female', icon: FemaleIcon },
    ] },
  { id: 'symptoms', part: 1, kind: 'multi', required: true,
    heading: <>Your <span style={{ color: ACCENT }}>current symptoms.</span></>,
    question: 'Do you experience any of the following?',
    options: [
      { value: 'low_libido', label: 'Low Libido / Erectile Dysfunction', icon: LibidoIcon, maleOnly: true },
      { value: 'low_desire', label: 'Low Desire / Painful Intimacy',      icon: LibidoIcon, femaleOnly: true },
      { value: 'hair_loss',  label: 'Hair Loss',                          icon: HairIcon },
      { value: 'skin',       label: 'Skin Problems',                      icon: SkinIcon },
      { value: 'brain_fog',  label: 'Brain Fog / Trouble Focusing',       icon: BrainIcon },
      { value: 'none',       label: 'None of these',                      icon: NoneIcon, exclusive: true },
    ] },
  { id: 'science', part: 1, kind: 'info',
    heading: <>How GLP-1 treatment <span style={{ color: ACCENT }}>is prescribed.</span></>,
    render: ScienceChart },
  { id: 'bodyShape', part: 1, kind: 'single', autoAdvance: true, grid: 2, required: true,
    heading: <>Tell us about your <span style={{ color: ACCENT }}>body type.</span></>,
    question: 'Which of the following body types is most like yours?',
    options: [
      { value: 'long_lean', label: 'Long and Lean',    icon: ShapeLean },
      { value: 'bell',      label: 'Bell Shaped',      icon: ShapeBell },
      { value: 'square',    label: 'Square Shaped',    icon: ShapeSquare },
      { value: 'inverted',  label: 'Inverted Triangle', icon: ShapeInv },
    ] },
  { id: 'motivation', part: 1, kind: 'single', autoAdvance: true, required: true,
    heading: <>Your <span style={{ color: ACCENT }}>treatment goals.</span></>,
    question: <>What is your <strong className="text-neutral-950">primary reason</strong> for wanting GLP-1 medication?</>,
    options: [
      { value: 'live_longer',   label: 'I want to live longer' },
      { value: 'feel_better',   label: 'I want to feel better' },
      { value: 'look_better',   label: 'I want to look better' },
      { value: 'reduce_issues', label: 'I want to reduce current health issues' },
    ] },
  { id: 'onGlp1', part: 1, kind: 'yesno',
    heading: <>Are you currently taking a <span style={{ color: ACCENT }}>GLP-1 medication?</span></>,
    subline: '(such as Ozempic, Wegovy, Zepbound, Mounjaro, Semaglutide, Tirzepatide)' },

  // ---------- Part 2 · Composition (8) ----------
  { id: 'takenBefore', part: 2, kind: 'yesno',
    heading: <>Have you <span style={{ color: ACCENT }}>ever taken</span> a GLP-1 in the past?</>,
    subline: 'This helps us decide your starting dose safely.' },
  { id: 'bmi', part: 2, kind: 'bmi',
    heading: <>Let&rsquo;s calculate your <span style={{ color: ACCENT }}>current BMI</span>.</> },
  { id: 'goalWeight', part: 2, kind: 'number', suffix: 'lbs', min: 60, max: 400, required: true,
    heading: <>Your <span style={{ color: ACCENT }}>goal weight.</span></>,
    question: 'What is your goal weight?',
    placeholder: 'Goal weight (lbs.)' },
  { id: 'activity', part: 2, kind: 'single', autoAdvance: true, required: true,
    heading: <>Your <span style={{ color: ACCENT }}>activity level.</span></>,
    question: 'How active are you on a typical week?',
    options: [
      { value: 'sedentary',   label: 'Mostly sedentary — desk-bound' },
      { value: 'light',       label: 'Light — 1-2 walks or workouts' },
      { value: 'moderate',    label: 'Moderate — 3-4 workouts a week' },
      { value: 'very_active', label: 'Very active — 5+ workouts a week' },
    ] },
  { id: 'sleepQuality', part: 2, kind: 'single', autoAdvance: true, grid: 2, required: true,
    heading: <>Tell us about your <span style={{ color: ACCENT }}>sleep.</span></>,
    question: 'How is your sleep?',
    options: [
      { value: 'good',     label: 'Pretty good',      icon: SleepGood },
      { value: 'restless', label: 'A bit restless',   icon: SleepRestless },
      { value: 'poor',     label: "I don't sleep well", icon: SleepPoor },
    ] },
  { id: 'sleepHours', part: 2, kind: 'single', autoAdvance: true, required: true,
    heading: <>How many hours of sleep do you usually get each night?</>,
    options: [
      { value: 'lt5',  label: 'Less than 5 hours' },
      { value: '6_7', label: '6–7 hours' },
      { value: '8_9', label: '8–9 hours' },
      { value: 'gt9',  label: 'More than 9 hours' },
    ] },

  // ---------- Part 3 · Screening (5) ----------
  { id: 'diabetesMeds', part: 3, kind: 'yesno',
    heading: 'Do you take any of the following medications?',
    subline: 'Insulin, glimepiride (Amaryl), glipizide (Glucotrol / XL), glyburide (Micronase, Glynase, Diabeta), sitagliptin, saxagliptin, linagliptin, and alogliptin.' },
  { id: 'conditions', part: 3, kind: 'multi', required: true, warn: true,
    heading: 'Current and past medical conditions',
    question: 'Please note: having any of the following medical conditions may disqualify you from a prescription.',
    options: [
      { value: 'none',        label: 'None of the below', exclusive: true },
      { value: 'ibd',         label: 'Inflammatory bowel disease' },
      { value: 'pancreatitis',label: 'Pancreatitis' },
      { value: 'pancreatic_cancer', label: 'Pancreatic cancer' },
      { value: 'type1',       label: 'Type 1 diabetes (Insulin-dependent diabetes)' },
      { value: 'thyroid_cancer', label: 'Thyroid cancer' },
      { value: 'family_thyroid', label: 'Family history of thyroid cancer' },
      { value: 'men2',        label: 'Family history of MEN-2 syndrome' },
      { value: 'anorexia',    label: 'Anorexia or bulemia' },
      { value: 'bypass_recent', label: 'Gastric bypass in the last 6 months' },
      { value: 'gastroparesis', label: 'Gastroparesis' },
    ] },
  { id: 'moreHealth', part: 3, kind: 'multi', required: true,
    heading: 'A few more health questions',
    question: 'Do any of the following apply to you?',
    options: [
      { value: 'none', label: 'None of the below', exclusive: true },
      { value: 'gallbladder', label: 'Gallbladder disease' },
      { value: 'hypertri',    label: 'Hypertriglyceridemia (elevated triglycerides)' },
      { value: 'kidney',      label: 'Kidney injury or kidney disease' },
      { value: 'pcos',        label: 'PCOS with fertility problems' },
      { value: 'eating_disorder', label: 'Eating disorder, past or present' },
      { value: 'suicidal',    label: 'Suicidal thoughts in the past 12 months' },
    ] },
  { id: 'pregnancy', part: 3, kind: 'single', autoAdvance: true, required: true, femaleOnly: true,
    heading: 'A quick safety check.',
    question: 'Are you pregnant, breastfeeding, or planning to become pregnant in the next 12 months?',
    options: [
      { value: 'no', label: 'No — none of the above' },
      { value: 'pregnant', label: 'Yes, I am pregnant' },
      { value: 'breastfeeding', label: 'Yes, I am breastfeeding' },
      { value: 'planning', label: 'Yes, planning to become pregnant' },
    ] },

  // ---------- Part 4 · History (1) ----------
  { id: 'glpHistory', part: 4, kind: 'single', autoAdvance: true, required: true,
    tagline: 'GLP-1 medication history',
    heading: 'Are you currently, or have you in the past two months, taken any of the following medications?',
    options: [
      { value: 'semaglutide', label: 'Semaglutide (Ozempic, Wegovy, Rybelsus)' },
      { value: 'tirzepatide', label: 'Tirzepatide (Zepbound, Mounjaro)' },
      { value: 'none',        label: 'None of these' },
    ] },

  // ---------- Part 5 · Prescription (8) ----------
  { id: 'medPreference', part: 5, kind: 'single', autoAdvance: true, required: true,
    heading: <>Choose the medication your <span style={{ color: ACCENT }}>provider should consider.</span></>,
    question: 'Which medication would you prefer?',
    options: [
      { value: 'semaglutide', label: 'Semaglutide — GLP-1 receptor agonist' },
      { value: 'tirzepatide', label: 'Tirzepatide — GLP-1 and GIP receptor agonist' },
      { value: 'provider',    label: 'No preference — my provider will decide' },
    ] },
  { id: 'form', part: 5, kind: 'single', autoAdvance: true, required: true,
    heading: <>Some prefer needles, others don&rsquo;t. <span style={{ color: ACCENT }}>What&rsquo;s your preference?</span></>,
    question: 'Preferred medication form',
    options: [
      { value: 'injection',  label: 'Weekly injection' },
      { value: 'sublingual', label: 'Sublingual (dissolves under the tongue)' },
      { value: 'either',     label: 'Either — provider decides' },
    ] },
  { id: 'planLength', part: 5, kind: 'single', autoAdvance: true, required: true,
    heading: <>Longer plans <span style={{ color: ACCENT }}>lock in a lower price.</span></>,
    question: 'How long a plan would you like to explore?',
    options: [
      { value: '1', label: 'Monthly' },
      { value: '3', label: '3-Month' },
      { value: '6', label: '6-Month' },
      { value: '12', label: '12-Month' },
    ] },
  { id: 'startWhen', part: 5, kind: 'single', autoAdvance: true, required: true,
    heading: <>How soon would you like to <span style={{ color: ACCENT }}>begin?</span></>,
    options: [
      { value: 'asap',    label: 'As soon as possible' },
      { value: 'weeks',   label: 'Within the next 2 weeks' },
      { value: 'month',   label: 'Within the next month' },
      { value: 'not_sure',label: "I'm still deciding" },
    ] },
  { id: 'shipping', part: 5, kind: 'single', autoAdvance: true, required: true,
    heading: <>How would you like it delivered?</>,
    options: [
      { value: 'standard', label: 'Standard shipping · included · typically 2–3 business days once prescribed' },
    ] },
  { id: 'supportive', part: 5, kind: 'single', autoAdvance: true, required: true,
    heading: <>Your provider may prescribe your medication formulated with one of the following <span style={{ color: ACCENT }}>ingredients.</span> Which would you prefer?</>,
    options: [
      { value: 'b12',     label: 'Vitamin B12' },
      { value: 'glycine', label: 'Glycine' },
    ] },
  { id: 'otherMeds', part: 5, kind: 'medList',
    heading: 'Are you currently prescribed any other medications?',
    subline: 'Simply add the names of any medicine you\'re currently prescribed, otherwise just tap "I don\'t take any medications."' },
  { id: 'sideEffectsAck', part: 5, kind: 'consent',
    heading: 'What you should know',
    subline: 'Read the following for more information about this product and its potential side effects.',
    disclosure: 'GENERAL GUIDELINES',
    body: 'GLP-1 medications may cause nausea, diarrhea, vomiting, constipation, abdominal pain, or decreased appetite. Rare but serious risks include pancreatitis, gallbladder disease, kidney problems, and thyroid tumors. Do not use if you or a family member have had medullary thyroid carcinoma or MEN-2. Discuss all risks with your prescribing provider before starting therapy.',
    ack: 'By checking this box, you acknowledge the above information' },

  // ---------- Part 6 · You (final: allergies → contact → consent) ----------
  { id: 'allergies', part: 6, kind: 'yesno', question: 'Do you have any allergies?',
    heading: <>Tell us about <span style={{ color: ACCENT }}>allergies.</span></>,
    subline: 'Include any allergies to food, dyes, prescription or over-the-counter medicines (e.g., antibiotics, allergy medications), herbs, vitamins, supplements, or anything else.' },
  { id: 'otherConditions', part: 6, kind: 'yesno',
    heading: <>Do you have any medical condition(s), a history of prior surgeries, or <span style={{ color: ACCENT }}>anything else</span> you want to tell your doctor?</> },
  { id: 'mindset', part: 6, kind: 'single', autoAdvance: true, required: true,
    heading: <>Let&rsquo;s better understand your <span style={{ color: ACCENT }}>current state of mind.</span></>,
    question: null, // dynamic
    options: [
      { value: 'ready',    label: "I'm ready" },
      { value: 'hopeful',  label: "I'm feeling hopeful" },
      { value: 'cautious', label: "I'm cautious" },
    ] },
  { id: 'birthSex', part: 6, kind: 'single', autoAdvance: true, required: true,
    heading: <>Sex <span style={{ color: ACCENT }}>assigned at birth.</span></>,
    question: 'Sex assigned at birth',
    subline: 'Your sex assigned at birth is important for your doctor to make medical decisions.',
    options: [
      { value: 'male',   label: 'Male' },
      { value: 'female', label: 'Female' },
    ] },
  { id: 'dob', part: 6, kind: 'dob',
    heading: <>When were you <span style={{ color: ACCENT }}>born?</span></>,
    subline: 'Weight-loss patients must be at least 18 years old.' },
  { id: 'contact', part: 6, kind: 'contact',
    heading: 'Last step: how can we reach you?',
    subline: 'Our physicians and pharmacy use email for patient communication.' },
  { id: 'basics', part: 6, kind: 'basics',
    heading: <>Just the <span style={{ color: ACCENT }}>basics.</span></>,
    subline: 'Please enter your mobile number and state.' },
  { id: 'truth', part: 6, kind: 'consent',
    tagline: 'Truthfulness consent',
    heading: 'Please attest to the following confirming that all information provided is true',
    disclosure: 'TRUTHFULNESS CONSENT',
    body: 'I certify that all information provided in this online visit is true and complete to the best of my knowledge. I understand that providing false information may result in denial of treatment, revocation of prescription, or other legal consequences. I authorize a licensed physician to review my responses to determine treatment eligibility.',
    ack: 'By checking this box, you acknowledge the above information' },
];

// ---------------------------------------------------------------------------
export default function Quiz() {
  const navigate = useNavigate();
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});

  // Filter steps for gender gating (pregnancy is female-only)
  const visibleSteps = useMemo(
    () => STEPS.filter((s) => !s.femaleOnly || (answers.sex === 'female' || answers.birthSex === 'female')),
    [answers.sex, answers.birthSex]
  );

  const step  = visibleSteps[idx];
  const total = visibleSteps.length;

  const currentPart = step.part;
  const partIdxs = visibleSteps.map((s, i) => ({ i, part: s.part })).filter((s) => s.part === currentPart).map((s) => s.i);
  const partPos   = partIdxs.indexOf(idx);
  const partTotal = partIdxs.length;
  const partProgress = ((partPos + 1) / partTotal) * 100;
  const partStepIndicator = `${partPos + 1} / ${partTotal}`;

  const setAnswer = (id, value) => setAnswers((a) => ({ ...a, [id]: value }));

  const canProceed = () => {
    if (step.kind === 'info') return true;
    const v = answers[step.id];
    if (step.kind === 'multi') return Array.isArray(v) && v.length > 0;
    if (step.kind === 'bmi')     return v?.feet && v?.inches !== undefined && v?.weight;
    if (step.kind === 'dob')     return v && /^\d{2}\/\d{2}\/\d{4}$/.test(v) && ageFrom(v) >= 18;
    if (step.kind === 'contact') return v?.email && /.+@.+\..+/.test(v.email);
    if (step.kind === 'basics')  return v?.phone && v?.state;
    if (step.kind === 'consent') return v === true;
    if (step.kind === 'medList') return v !== undefined; // array or 'skip'
    if (step.kind === 'yesno')   return v === 'yes' || v === 'no';
    if (step.kind === 'number')  return v && Number(v) >= (step.min || 1);
    return !!v;
  };

  const finish = () => {
    try {
      localStorage.setItem('alfa_quiz_weight-loss', JSON.stringify({ answers, at: new Date().toISOString() }));
      localStorage.setItem('alfa_quiz_last', 'weight-loss');
    } catch { /* storage unavailable */ }
    const email = answers.contact?.email;
    const name  = answers.contact?.name;
    submitQuiz({ variant: 'weight-loss', answers, email, name }).catch(() => {});
    navigate('/weight-loss/qualifying');
  };

  const advance = () => {
    if (idx < total - 1) setIdx(idx + 1);
    else finish();
  };
  const next = () => { if (canProceed()) advance(); };
  const back = () => (idx > 0 ? setIdx(idx - 1) : navigate('/'));

  return (
    <div className="min-h-screen relative">
      <div className="telegra-bg" aria-hidden="true" />

      <header className="sticky top-0 z-40 backdrop-blur-md border-b border-neutral-900/5 bg-white/85">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-3 pb-2 flex items-center justify-between">
          <Link to="/" data-testid="quiz-logo-home" className="flex items-center">
            <img
              src="/images/alfa-logo-black.png?v=2"
              alt="ALFÀ"
              className="h-7 sm:h-8 w-auto object-contain"
            />
          </Link>
          <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-[0.2em] text-neutral-950">
            <ShieldCheck size={12} className="text-emerald-600" /> HIPAA · Discreet
          </div>
        </div>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 pb-2.5">
          <div className="flex items-center gap-1.5" data-testid="quiz-part-track">
            {PARTS.map((p) => {
              const done   = p.n < currentPart;
              const active = p.n === currentPart;
              return (
                <div key={p.n} data-testid={`quiz-part-${p.n}`}
                     className={`flex-1 h-1 rounded-full overflow-hidden ${done ? 'bg-neutral-900' : 'bg-neutral-900/10'}`}>
                  {active && (
                    <div className="h-full bg-neutral-900 transition-all duration-500 ease-out"
                         style={{ width: `${partProgress}%` }} />
                  )}
                </div>
              );
            })}
          </div>
          <div className="mt-1.5 flex items-center justify-between">
            <p className="uppercase text-[10.5px] tracking-[0.24em] text-neutral-950">
              Part <span className="text-neutral-900 font-semibold">{currentPart}</span> of 6 · {PARTS[currentPart - 1].label}
            </p>
            <p className="uppercase text-[10.5px] tracking-[0.24em] text-neutral-400" data-testid="quiz-step-indicator">
              {partStepIndicator}
            </p>
          </div>
        </div>
      </header>

      <main className="relative max-w-3xl mx-auto px-4 sm:px-6 pt-3 sm:pt-4 pb-6">
        <button onClick={back} data-testid="quiz-back-btn"
                className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] font-semibold text-neutral-950 hover:text-neutral-900 mb-2.5">
          <ArrowLeft size={12} /> {idx === 0 ? 'Exit' : 'Back'}
        </button>

        <StepBlock
          key={step.id}
          step={step}
          answers={answers}
          value={answers[step.id]}
          onChange={(v) => {
            setAnswer(step.id, v);
            if (step.autoAdvance) setTimeout(() => setIdx((i) => Math.min(i + 1, total - 1)), 240);
          }}
          onYes={() => { setAnswer(step.id, 'yes'); setTimeout(advance, 200); }}
          onNo={()  => { setAnswer(step.id, 'no');  setTimeout(advance, 200); }}
          onSkipMeds={() => { setAnswer(step.id, 'skip'); setTimeout(advance, 200); }}
        />

        {!step.autoAdvance && step.kind !== 'yesno' && step.kind !== 'medList' && (
          <div className="mt-4 flex justify-end">
            <button onClick={next} disabled={!canProceed()} data-testid="quiz-next-btn"
                    className={`inline-flex items-center gap-2 uppercase text-[12px] tracking-[0.18em] font-semibold px-6 py-2.5 rounded-full transition-all ${canProceed() ? 'bg-neutral-950 text-white hover:bg-neutral-800 shadow-lg shadow-neutral-950/10' : 'bg-neutral-300 text-neutral-950 cursor-not-allowed'}`}>
              {idx === total - 1 ? 'See my results' : 'Continue'}
              <ArrowRight size={13} />
            </button>
          </div>
        )}

        {/* Trust strip — appears beneath every step to lift completion */}
        <p data-testid="quiz-trust-strip" className="mt-4 text-center text-[11px] uppercase tracking-[0.22em] font-semibold text-neutral-950 flex items-center justify-center gap-2 flex-wrap">
          <ShieldCheck size={12} className="text-emerald-600" />
          Licensed U.S. providers
          <span className="text-neutral-300">·</span>
          HIPAA-secure intake
        </p>
      </main>

      <style>{`
        @keyframes fadeUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        .anim-fadeup { animation: fadeUp 500ms ease both; }
        input[type=number]::-webkit-outer-spin-button, input[type=number]::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
        input[type=number] { -moz-appearance: textfield; }
      `}</style>
    </div>
  );
}

// ---------------------------------------------------------------------------
function StepBlock({ step, answers, value, onChange, onYes, onNo, onSkipMeds }) {
  // dynamic headings & questions
  let heading = step.heading;
  let question = step.question;

  if (step.id === 'mindset') {
    const gw = answers.goalWeight;
    question = <>How motivated are you to reach {gw ? <strong className="text-neutral-950" style={{ color: ACCENT }}>{gw} lbs</strong> : <strong style={{ color: ACCENT }}>your goal</strong>}?</>;
  }

  return (
    <div className="anim-fadeup">
      {step.tagline && (
        <p className="uppercase text-[11px] tracking-[0.24em] font-semibold text-neutral-950 mb-2">{step.tagline}</p>
      )}
      {heading && (
        <h1 className="font-display uppercase text-[20px] sm:text-[26px] md:text-[30px] leading-[1.05] tracking-[-0.02em] text-[color:var(--alfa-navy)] max-w-2xl">
          {heading}
        </h1>
      )}
      {step.subline && (
        <p className="mt-2 text-[13px] sm:text-[13.5px] text-neutral-950 max-w-xl leading-snug">{step.subline}</p>
      )}
      {question && (
        <div className="mt-3">
          <h2 className="text-[14px] sm:text-[15px] font-semibold text-[color:var(--alfa-navy)]">
            {question}{step.required && <span className="text-red-500 ml-1">*</span>}
          </h2>
        </div>
      )}
      {step.warn && (
        <p className="mt-2 inline-flex items-start gap-2 text-[12px] text-red-700 bg-red-50 border border-red-100 rounded-md px-3 py-1.5">
          <AlertCircle size={13} className="mt-0.5 shrink-0" />
          <span>{step.question || 'Please review carefully.'}</span>
        </p>
      )}

      <div className="mt-3">
        {step.kind === 'single'  && <SingleChoice step={step} value={value} onChange={onChange} />}
        {step.kind === 'tiles'   && <TileChoice  step={step} value={value} onChange={onChange} />}
        {step.kind === 'multi'   && <MultiChoice step={step} answers={answers} value={value || []} onChange={onChange} />}
        {step.kind === 'yesno'   && <YesNo value={value} onYes={onYes} onNo={onNo} />}
        {step.kind === 'info'    && step.render && <step.render answers={answers} />}
        {step.kind === 'number'  && <NumberInput step={step} value={value} onChange={onChange} />}
        {step.kind === 'bmi'     && <BmiInput   value={value} onChange={onChange} />}
        {step.kind === 'dob'     && <DobInput   value={value} onChange={onChange} />}
        {step.kind === 'contact' && <ContactForm value={value} onChange={onChange} />}
        {step.kind === 'basics'  && <BasicsForm  value={value} onChange={onChange} />}
        {step.kind === 'medList' && <MedList value={value} onChange={onChange} onSkip={onSkipMeds} />}
        {step.kind === 'consent' && <ConsentBlock step={step} value={value} onChange={onChange} />}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Choice components ----------------------------------------------------------
function SingleChoice({ step, value, onChange }) {
  const gridClass = step.grid === 2 ? 'grid-cols-2' : 'grid-cols-1';
  return (
    <div className={`grid ${gridClass} gap-2`}>
      {step.options.map((o) => {
        const active = value === o.value;
        return (
          <button key={o.value} data-testid={`quiz-opt-${step.id}-${o.value}`} onClick={() => onChange(o.value)}
            className={`text-left w-full rounded-xl border transition-all px-4 py-3 flex items-center gap-3 ${active ? 'border-neutral-950 bg-neutral-950 text-white shadow-lg' : 'border-neutral-900/10 bg-white/70 hover:bg-white hover:border-neutral-400'}`}>
            {o.icon && (
              <span className={`shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${active ? 'bg-white/10' : 'bg-neutral-100'}`}>
                <o.icon color={active ? '#fff' : ACCENT} size={26} />
              </span>
            )}
            <span className={`text-[14.5px] font-semibold flex-1 ${active ? 'text-white' : 'text-neutral-900'}`}>{o.label}</span>
            <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${active ? 'border-white bg-white' : 'border-neutral-300'}`}>
              {active && <span className="w-1.5 h-1.5 rounded-full bg-neutral-950" />}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function TileChoice({ step, value, onChange }) {
  return (
    <div className="grid grid-cols-2 gap-3 max-w-lg">
      {step.options.map((o) => {
        const active = value === o.value;
        return (
          <button key={o.value} data-testid={`quiz-opt-${step.id}-${o.value}`} onClick={() => onChange(o.value)}
            className={`aspect-[4/3] rounded-xl border-2 transition-all flex flex-col items-center justify-center gap-2 ${active ? 'border-neutral-950 bg-white shadow-xl' : 'border-neutral-900/10 bg-white/60 hover:bg-white'}`}>
            <o.icon color={active ? '#0a0a0a' : ACCENT} size={48} />
            <span className={`text-[14px] font-semibold ${active ? 'text-neutral-950' : 'text-neutral-950'}`}>{o.label}</span>
          </button>
        );
      })}
    </div>
  );
}

function MultiChoice({ step, answers, value, onChange }) {
  const visible = step.options.filter((o) => {
    if (o.maleOnly && answers.sex !== 'male') return false;
    if (o.femaleOnly && answers.sex !== 'female') return false;
    return true;
  });
  const toggle = (v, opt) => {
    if (opt.exclusive) { onChange(value.includes(v) ? [] : [v]); return; }
    let next = value.filter((x) => x !== v && !visible.find((o) => o.value === x)?.exclusive);
    if (!value.includes(v)) next = [...next, v];
    onChange(next);
  };
  const hasIcons = visible.some((o) => o.icon);
  const gridClass = hasIcons ? 'grid-cols-2 sm:grid-cols-3' : 'grid-cols-1';
  return (
    <div className={`grid ${gridClass} gap-2`}>
      {visible.map((o) => {
        const active = value.includes(o.value);
        if (hasIcons) {
          return (
            <button key={o.value} data-testid={`quiz-opt-${step.id}-${o.value}`} onClick={() => toggle(o.value, o)}
              className={`relative rounded-xl border-2 transition-all flex flex-col items-center justify-center text-center gap-1.5 px-2 py-3 min-h-[86px] ${active ? 'border-neutral-950 bg-white shadow' : 'border-neutral-900/10 bg-white/60 hover:bg-white'}`}>
              <o.icon color={active ? '#0a0a0a' : ACCENT} size={30} />
              <span className={`text-[12px] font-semibold leading-tight ${active ? 'text-neutral-950' : 'text-neutral-950'}`}>{o.label}</span>
              {active && (
                <span className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full bg-neutral-950 flex items-center justify-center">
                  <Check size={9} className="text-white" strokeWidth={3} />
                </span>
              )}
            </button>
          );
        }
        return (
          <button key={o.value} data-testid={`quiz-opt-${step.id}-${o.value}`} onClick={() => toggle(o.value, o)}
            className={`text-left rounded-lg border-2 transition-all px-4 py-2.5 flex items-center gap-3 ${active ? 'border-neutral-950 bg-white shadow-sm' : 'border-neutral-900/10 bg-white/60 hover:bg-white'}`}>
            <span className={`w-4 h-4 rounded-sm border-2 flex items-center justify-center shrink-0 ${active ? 'border-neutral-950 bg-neutral-950' : 'border-neutral-400'}`}>
              {active && <Check size={10} className="text-white" strokeWidth={3} />}
            </span>
            <span className={`text-[13.5px] font-medium ${active ? 'text-neutral-950' : 'text-neutral-950'}`}>{o.label}</span>
          </button>
        );
      })}
    </div>
  );
}

function YesNo({ value, onYes, onNo }) {
  return (
    <div className="grid grid-cols-2 gap-3 max-w-lg">
      <button onClick={onYes} data-testid="quiz-yes"
        className={`py-3.5 rounded-xl border-2 text-[15px] font-semibold transition-all ${value === 'yes' ? 'bg-neutral-950 border-neutral-950 text-white' : 'bg-white/70 border-neutral-900/10 hover:border-neutral-400'}`}>
        Yes
      </button>
      <button onClick={onNo} data-testid="quiz-no"
        className={`py-3.5 rounded-xl border-2 text-[15px] font-semibold transition-all ${value === 'no' ? 'bg-neutral-950 border-neutral-950 text-white' : 'bg-white/70 border-neutral-900/10 hover:border-neutral-400'}`}>
        No
      </button>
    </div>
  );
}

function NumberInput({ step, value, onChange }) {
  return (
    <div className="relative max-w-sm">
      <input type="number" inputMode="numeric" min={step.min} max={step.max} value={value || ''}
             data-testid={`quiz-input-${step.id}`}
             onChange={(e) => onChange(e.target.value)}
             placeholder={step.placeholder}
             className="w-full px-5 py-5 pr-16 text-[24px] font-semibold rounded-2xl border-2 border-neutral-900/10 bg-white/80 focus:outline-none focus:border-neutral-950" />
      {step.suffix && (
        <span className="absolute right-5 top-1/2 -translate-y-1/2 text-[14px] uppercase tracking-[0.16em] text-neutral-950 flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full" style={{ background: ACCENT }} />
          {step.suffix}
        </span>
      )}
    </div>
  );
}

function BmiInput({ value = {}, onChange }) {
  const bmi = useMemo(() => {
    const w = parseFloat(value.weight);
    const f = parseFloat(value.feet);
    const i = parseFloat(value.inches);
    const inches = (f || 0) * 12 + (i || 0);
    if (!w || !inches) return 0;
    return (w * 703) / (inches * inches);
  }, [value.feet, value.inches, value.weight]);
  const bmiClamped = Math.max(15, Math.min(45, bmi || 15));
  const bmiPct = ((bmiClamped - 15) / 30) * 100;

  return (
    <div>
      <div className="text-center rounded-3xl bg-white/70 border border-neutral-900/10 p-6 sm:p-8">
        <p className="uppercase text-[11px] tracking-[0.24em] text-neutral-950">Your BMI</p>
        <p className="mt-2 font-display text-[64px] sm:text-[80px] leading-none text-neutral-950" data-testid="quiz-bmi-value">
          {bmi ? bmi.toFixed(1) : '0.0'}
        </p>
        <div className="mt-6 relative h-2.5 rounded-full overflow-hidden"
             style={{ background: 'linear-gradient(90deg, #10b981 0%, #f59e0b 50%, #ef4444 100%)' }}>
          <div className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-white border-2 border-neutral-950 rounded-full shadow"
               style={{ left: `calc(${bmiPct}% - 8px)` }} />
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-3">
        <LabeledInput label="Feet" testid="quiz-input-feet" value={value.feet ?? ''} min={3} max={8}
                      onChange={(v) => onChange({ ...value, feet: v })} />
        <LabeledInput label="Inches" testid="quiz-input-inches" value={value.inches ?? ''} min={0} max={11}
                      onChange={(v) => onChange({ ...value, inches: v })} />
      </div>
      <div className="mt-3">
        <LabeledInput label="Weight (in lbs)" testid="quiz-input-weight" value={value.weight ?? ''} min={60} max={600}
                      onChange={(v) => onChange({ ...value, weight: v })} />
      </div>
    </div>
  );
}

function LabeledInput({ label, value, onChange, min, max, testid }) {
  return (
    <label className="block">
      <span className="block text-[13px] font-semibold text-neutral-900 mb-1.5">{label}</span>
      <input type="number" inputMode="numeric" min={min} max={max} value={value}
             data-testid={testid}
             onChange={(e) => onChange(e.target.value)}
             className="w-full px-4 py-4 text-[17px] font-semibold rounded-xl border-2 border-neutral-900/10 bg-white/80 focus:outline-none focus:border-neutral-950" />
    </label>
  );
}

function DobInput({ value, onChange }) {
  return (
    <input type="text" placeholder="MM/DD/YYYY" value={value || ''} data-testid="quiz-input-dob"
           onChange={(e) => {
             let v = e.target.value.replace(/[^\d]/g, '');
             if (v.length > 2) v = v.slice(0, 2) + '/' + v.slice(2);
             if (v.length > 5) v = v.slice(0, 5) + '/' + v.slice(5, 9);
             onChange(v);
           }}
           className="w-full max-w-sm px-5 py-5 text-[20px] font-semibold rounded-2xl border-2 border-neutral-900/10 bg-white/80 focus:outline-none focus:border-neutral-950" />
  );
}

function ContactForm({ value = {}, onChange }) {
  return (
    <div className="max-w-md space-y-3">
      <label className="block">
        <span className="block text-[13px] font-semibold text-neutral-900 mb-1.5">Full name</span>
        <input type="text" value={value.name || ''} data-testid="quiz-input-name"
               onChange={(e) => onChange({ ...value, name: e.target.value })}
               className="w-full px-4 py-4 text-[16px] rounded-xl border-2 border-neutral-900/10 bg-white/80 focus:outline-none focus:border-neutral-950" />
      </label>
      <label className="block">
        <span className="block text-[13px] font-semibold text-neutral-900 mb-1.5">Email address</span>
        <input type="email" value={value.email || ''} data-testid="quiz-input-email"
               onChange={(e) => onChange({ ...value, email: e.target.value })}
               className="w-full px-4 py-4 text-[16px] rounded-xl border-2 border-neutral-900/10 bg-white/80 focus:outline-none focus:border-neutral-950" />
      </label>
      <p className="pt-1 inline-flex items-start gap-2 text-[12.5px] text-neutral-950">
        <ShieldCheck size={14} className="text-emerald-600 mt-0.5 shrink-0" />
        ALFÀ takes your privacy seriously with industry-leading encryption.
      </p>
    </div>
  );
}

function BasicsForm({ value = {}, onChange }) {
  return (
    <div className="max-w-md space-y-3">
      <label className="block">
        <span className="block text-[13px] font-semibold text-neutral-900 mb-1.5">Phone</span>
        <input type="tel" value={value.phone || ''} data-testid="quiz-input-phone" placeholder="(555) 555-5555"
               onChange={(e) => onChange({ ...value, phone: e.target.value })}
               className="w-full px-4 py-4 text-[16px] rounded-xl border-2 border-neutral-900/10 bg-white/80 focus:outline-none focus:border-neutral-950" />
      </label>
      <label className="block relative">
        <span className="block text-[13px] font-semibold text-neutral-900 mb-1.5">State</span>
        <select value={value.state || ''} data-testid="quiz-input-state"
                onChange={(e) => onChange({ ...value, state: e.target.value })}
                className="w-full appearance-none px-4 py-4 text-[16px] rounded-xl border-2 border-neutral-900/10 bg-white/80 focus:outline-none focus:border-neutral-950">
          <option value="" disabled>Select your state</option>
          {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <ChevronDown size={16} className="absolute right-4 bottom-4 text-neutral-950 pointer-events-none" />
      </label>
      <p className="pt-1 text-[12px] text-neutral-950 leading-relaxed">
        By entering your phone number, you consent to ALFÀ contacting you for order and health-related communications. Message & data rates may apply. Reply STOP to opt out.
      </p>
    </div>
  );
}

function MedList({ value, onChange, onSkip }) {
  const [text, setText] = useState('');
  const list = Array.isArray(value) ? value : [];
  const add = () => {
    if (!text.trim()) return;
    onChange([...list, text.trim()]);
    setText('');
  };
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));

  return (
    <div className="max-w-lg">
      <div className="rounded-2xl border-2 border-neutral-900/10 bg-white/70 p-4">
        {list.length === 0 ? (
          <p className="text-[14px] text-neutral-950 italic">There are no medications added.</p>
        ) : (
          <ul className="space-y-2">
            {list.map((m, i) => (
              <li key={i} className="flex items-center justify-between rounded-lg bg-white border border-neutral-900/10 px-3 py-2.5">
                <span className="text-[14px] text-neutral-900 font-medium">{m}</span>
                <button onClick={() => remove(i)} data-testid={`med-remove-${i}`} className="text-neutral-400 hover:text-red-500">
                  <Trash2 size={14} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="mt-3 flex gap-2">
        <input type="text" value={text} placeholder="Medication name" data-testid="quiz-med-input"
               onChange={(e) => setText(e.target.value)}
               onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
               className="flex-1 px-4 py-3 text-[15px] rounded-xl border-2 border-neutral-900/10 bg-white/80 focus:outline-none focus:border-neutral-950" />
        <button onClick={add} data-testid="quiz-med-add"
                className="inline-flex items-center gap-2 uppercase text-[12px] tracking-[0.14em] font-semibold px-5 rounded-xl bg-neutral-950 text-white hover:bg-neutral-800">
          <Plus size={14} /> Add
        </button>
      </div>
      <div className="mt-6 grid grid-cols-1 gap-3">
        {list.length > 0 && (
          <button onClick={() => onChange(list)} data-testid="quiz-med-continue"
                  className="inline-flex items-center justify-center gap-2 uppercase text-[13px] tracking-[0.18em] font-semibold px-8 py-4 rounded-full bg-neutral-950 text-white hover:bg-neutral-800">
            Continue <ArrowRight size={15} />
          </button>
        )}
        <button onClick={onSkip} data-testid="quiz-med-skip"
                className="inline-flex items-center justify-center gap-2 uppercase text-[13px] tracking-[0.18em] font-semibold px-8 py-4 rounded-full bg-white border-2 border-neutral-900/10 text-neutral-900 hover:border-neutral-400">
          I don&rsquo;t take any medications
        </button>
      </div>
    </div>
  );
}

function ConsentBlock({ step, value, onChange }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="max-w-2xl">
      <div className="rounded-2xl border-2 border-neutral-900/10 bg-white/70 overflow-hidden">
        <button onClick={() => setOpen((o) => !o)} data-testid="quiz-consent-toggle"
                className="w-full flex items-center justify-between px-5 py-4 text-left">
          <span className="text-[13px] uppercase tracking-[0.2em] font-semibold text-neutral-900">{step.disclosure}</span>
          <ChevronDown size={18} className={`text-neutral-950 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
        {open && (
          <div className="px-5 pb-5 text-[14px] text-neutral-950 leading-relaxed border-t border-neutral-900/5 pt-4">
            {step.body}
          </div>
        )}
      </div>
      <label className="mt-4 flex items-start gap-3 cursor-pointer">
        <input type="checkbox" checked={value === true} data-testid="quiz-consent-check"
               onChange={(e) => onChange(e.target.checked)}
               className="mt-0.5 w-5 h-5 rounded border-2 border-neutral-400 accent-neutral-950" />
        <span className="text-[14px] text-neutral-950">{step.ack}</span>
      </label>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Info-screen renderers
// ---------------------------------------------------------------------------
function ScienceChart() {
  return (
    <div className="anim-fadeup">
      <p className="text-[13.5px] leading-snug text-neutral-950">
        <strong className="text-neutral-950">Semaglutide</strong> and <strong className="text-neutral-950">tirzepatide</strong> may be prescribed by a <strong className="text-neutral-950">licensed provider</strong> when medically appropriate and are dispensed by licensed pharmacies. Compounded medications are not FDA-approved. Individual results vary.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// helpers
function ageFrom(mmddyyyy) {
  if (!mmddyyyy) return 0;
  const [mm, dd, yyyy] = mmddyyyy.split('/').map(Number);
  if (!mm || !dd || !yyyy) return 0;
  const d = new Date(yyyy, mm - 1, dd);
  const diff = Date.now() - d.getTime();
  return Math.floor(diff / (365.25 * 24 * 3600 * 1000));
}

// ---------------------------------------------------------------------------
// Inline icons — champagne-toned illustrations
// ---------------------------------------------------------------------------
function MaleIcon({ color = ACCENT, size = 88 }) {
  return (<svg width={size} height={size} viewBox="0 0 64 64" fill="none"><circle cx="32" cy="14" r="7.5" fill={color} /><path d="M20 27 h24 l-3 22 h-6 v11 h-6 V49 h-6 z" fill={color} /></svg>);
}
function FemaleIcon({ color = ACCENT, size = 88 }) {
  return (<svg width={size} height={size} viewBox="0 0 64 64" fill="none"><circle cx="32" cy="14" r="7.5" fill={color} /><path d="M32 24 l14 26 h-8 v10 h-12 V50 h-8 z" fill={color} /></svg>);
}
function LibidoIcon({ color = ACCENT, size = 44 }) {
  return (<svg width={size} height={size} viewBox="0 0 48 48" fill="none"><path d="M8 12 L24 28 L40 12" stroke={color} strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" /><path d="M24 28 v14 M20 38 l4 4 l4 -4" stroke={color} strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" /></svg>);
}
function HairIcon({ color = ACCENT, size = 44 }) {
  return (<svg width={size} height={size} viewBox="0 0 48 48" fill="none"><path d="M14 6 L14 32 M18 6 L18 32 M22 6 L22 32 M26 6 L26 32 M30 6 L30 32" stroke={color} strokeWidth="2.5" strokeLinecap="round" /><path d="M8 32 h32 v4 h-32 z" fill={color} /><path d="M22 40 l-4 4 M18 40 l-2 3 M26 42 l3 4" stroke={color} strokeWidth="2.5" strokeLinecap="round" /></svg>);
}
function SkinIcon({ color = ACCENT, size = 44 }) {
  return (<svg width={size} height={size} viewBox="0 0 48 48" fill="none"><circle cx="24" cy="24" r="15" stroke={color} strokeWidth="2.5" fill="none" /><circle cx="18" cy="20" r="1.6" fill={color} /><circle cx="30" cy="18" r="1.6" fill={color} /><circle cx="22" cy="30" r="1.6" fill={color} /><circle cx="32" cy="28" r="1.6" fill={color} /><circle cx="15" cy="27" r="1.6" fill={color} /></svg>);
}
function BrainIcon({ color = ACCENT, size = 44 }) {
  return (<svg width={size} height={size} viewBox="0 0 48 48" fill="none"><path d="M17 10 c -6 0 -8 6 -6 10 c -3 3 -1 8 3 9 c 0 4 4 6 8 5 v -22 c -2 -2 -3 -2 -5 -2 z" stroke={color} strokeWidth="2.5" fill="none" /><path d="M31 10 c 6 0 8 6 6 10 c 3 3 1 8 -3 9 c 0 4 -4 6 -8 5 v -22 c 2 -2 3 -2 5 -2 z" stroke={color} strokeWidth="2.5" fill="none" /><path d="M22 22 h 4 M22 28 h 4" stroke={color} strokeWidth="2" /></svg>);
}
function NoneIcon({ color = ACCENT, size = 44 }) {
  return (<svg width={size} height={size} viewBox="0 0 48 48" fill="none"><circle cx="24" cy="24" r="15" fill={color} /><path d="M18 18 l12 12 M30 18 l -12 12" stroke="#fff" strokeWidth="3" strokeLinecap="round" /></svg>);
}
function ShapeLean({ color = ACCENT, size = 72 }) {
  return (<svg width={size} height={size} viewBox="0 0 64 96" fill="none"><rect x="22" y="12" width="20" height="72" rx="6" fill={color} /></svg>);
}
function ShapeBell({ color = ACCENT, size = 72 }) {
  return (<svg width={size} height={size} viewBox="0 0 64 96" fill="none"><path d="M32 8 C 20 8 18 32 18 52 C 18 78 22 88 32 88 C 42 88 46 78 46 52 C 46 32 44 8 32 8 z" fill={color} /></svg>);
}
function ShapeSquare({ color = ACCENT, size = 72 }) {
  return (<svg width={size} height={size} viewBox="0 0 64 96" fill="none"><rect x="12" y="16" width="40" height="64" rx="6" fill={color} /></svg>);
}
function ShapeInv({ color = ACCENT, size = 72 }) {
  return (<svg width={size} height={size} viewBox="0 0 64 96" fill="none"><path d="M8 14 L 56 14 L 34 86 L 30 86 z" fill={color} /></svg>);
}
function SleepGood({ color = ACCENT, size = 72 }) {
  return (<svg width={size} height={size} viewBox="0 0 64 64" fill="none"><path d="M6 44 h52 v10 h-4 v-4 h-44 v4 h-4 z" fill={color} /><rect x="16" y="30" width="30" height="14" rx="3" fill={color} /><circle cx="24" cy="26" r="5" fill={color} /><text x="34" y="20" fontSize="10" fontWeight="700" fill={color}>Z</text></svg>);
}
function SleepRestless({ color = ACCENT, size = 72 }) {
  return (<svg width={size} height={size} viewBox="0 0 64 64" fill="none"><path d="M6 44 h52 v10 h-4 v-4 h-44 v4 h-4 z" fill={color} /><rect x="14" y="28" width="28" height="16" rx="3" fill={color} /><path d="M22 30 c 0 -6 6 -8 6 -14" stroke={color} strokeWidth="2.5" fill="none" /><circle cx="28" cy="14" r="3" fill={color} /></svg>);
}
function SleepPoor({ color = ACCENT, size = 72 }) {
  return (<svg width={size} height={size} viewBox="0 0 64 64" fill="none"><path d="M6 44 h52 v10 h-4 v-4 h-44 v4 h-4 z" fill={color} /><path d="M14 44 h20 v-10 h-4 v-6 h-4 v-6 c 0 -3 -2 -6 -6 -6 s -6 3 -6 6" stroke={color} strokeWidth="2.5" fill="none" strokeLinecap="round" /><circle cx="18" cy="20" r="4" fill={color} /></svg>);
}
